<template id="appAdminGroupIcon">
  <div id="groupIconPage">
    <div id="groupIconHeader">
      <multiselect
        v-model="selectedGroupOption"
        :allow-empty="false"
        class="dropDown"
        deselect-label=""
        label="label"
        :options="groupOptions"
        placeholder="Start typing to search a group..."
        :searchable="true"
        select-label=""
        selected-label=""
        track-by="id"
      ></multiselect>
    </div>

    <div v-if="selectedGroupOption" id="groupIconContent">
      <div class="iconContainer">
        <div class="icon" :style="{ backgroundImage: iconStyle }">
          <div v-if="loading" class="placeholder">Loading…</div>
          <div v-else-if="!iconUrl" class="placeholder">No icon</div>
        </div>
      </div>

      <div class="actions">
        <md-button class="md-primary md-raised" @click="selectIconFile">
          {{ iconUrl ? "Replace icon" : "Upload icon" }}
        </md-button>
        <md-button
          v-if="iconUrl"
          class="md-primary md-raised"
          @click="showDeleteConfirm = true"
        >
          Delete icon
        </md-button>
        <input
          id="selectGroupIcon"
          accept="image/png, image/jpeg, image/gif, image/svg+xml"
          hidden
          type="file"
          @change="onIconSelected($event)"
        />
      </div>
    </div>

    <md-dialog :md-active.sync="showDeleteConfirm">
      <md-dialog-title>Delete group icon?</md-dialog-title>
      <md-dialog-content>
        <div>
          You are about to delete the icon of
          {{ selectedGroupOption && selectedGroupOption.label }}.
        </div>
        <div class="spacer"></div>
        <div>Are you sure?</div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="deleteIcon"
          >Delete</md-button
        >
        <md-button class="md-primary" @click="showDeleteConfirm = false"
          >Cancel</md-button
        >
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import multiselect from "vue-multiselect";
import appSettings from "./appSettings";

export default {
  name: "AppAdminGroupIcon",
  components: {
    multiselect,
  },
  data: () => ({
    groupOptions: [],
    selectedGroupOption: null,
    iconUrl: null,
    createdBlobUrl: null,
    loading: false,
    showDeleteConfirm: false,
  }),
  computed: {
    ...mapState(["idToken"]),
    iconStyle() {
      return this.iconUrl ? `url(${this.iconUrl})` : "";
    },
  },
  watch: {
    selectedGroupOption() {
      this.loadIcon();
    },
  },
  async created() {
    const groups = await this.loadResources({ resourceType: "groups" });
    this.groupOptions = groups.map((g) => ({
      id: g.id,
      label: (g.metadata && g.metadata.displayName) || g.id,
    }));
  },
  beforeDestroy() {
    this.revokeObjectUrl();
  },
  methods: {
    ...mapActions(["loadResources", "postAzureForm", "deleteAzure"]),
    ...mapMutations(["setErrorMessage"]),
    iconEndpoint() {
      return `${appSettings.remoteurl}/api/groups/${this.selectedGroupOption.id}/icon`;
    },
    async loadIcon() {
      this.revokeObjectUrl();
      this.iconUrl = null;
      if (!this.selectedGroupOption) return;

      this.loading = true;
      try {
        const response = await fetch(this.iconEndpoint(), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.idToken}`,
            "Session-Id": `${window.localStorage["sessionId"] ?? ""}`,
          },
        });

        // 204 means no icon is set for this group.
        if (response.status === 204) return;

        if (!response.ok) {
          throw new Error(
            `Icon request failed (${response.status} ${response.statusText})`
          );
        }

        const blob = await response.blob();
        this.createdBlobUrl = URL.createObjectURL(blob);
        this.iconUrl = this.createdBlobUrl;
      } catch (e) {
        this.setErrorMessage({
          message: "Could not load icon",
          details: e.message,
        });
      } finally {
        this.loading = false;
      }
    },
    selectIconFile() {
      document.querySelector("#selectGroupIcon").click();
    },
    async onIconSelected(event) {
      if (event.target.files.length === 0) return;

      const file = event.target.files[0];
      const formData = new FormData();
      formData.append(file.name, file);
      await this.postAzureForm({
        url: this.iconEndpoint(),
        formData,
        responseType: "text",
      });
      // Reset the input so selecting the same file again re-triggers change.
      event.target.value = "";
      await this.loadIcon();
    },
    async deleteIcon() {
      this.showDeleteConfirm = false;
      await this.deleteAzure({ url: this.iconEndpoint() });
      await this.loadIcon();
    },
    revokeObjectUrl() {
      if (this.createdBlobUrl) {
        URL.revokeObjectURL(this.createdBlobUrl);
        this.createdBlobUrl = null;
      }
    },
  },
};
</script>

<style scoped>
#groupIconPage {
  display: flex;
  flex-direction: column;
}
#groupIconHeader {
  display: flex;
  flex-direction: row;
}
.dropDown {
  width: 300px;
  margin-left: 10px;
  border-radius: 10px;
  padding-left: 6px;
  padding-top: 8px;
  margin-bottom: 20px;
}
#groupIconContent {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 20px;
}
.iconContainer {
  margin: 20px;
}
.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22vh;
  width: 22vh;
  border-radius: 12px;
  background-color: black;
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 100%;
}
.placeholder {
  color: white;
  opacity: 0.6;
}
.actions {
  display: flex;
  flex-direction: column;
}
.spacer {
  height: 10px;
}
::v-deep .multiselect__select {
  margin-top: 8px;
}
</style>
